import { SalesTaxFiling, useFilingsList } from '@commenda/api';
import { NonTimezoneSensitiveDate } from '@commenda/utils/non-time-zone-sensitive-date';
import {
  SalesTaxFilingFrequencyEnum,
  SalesTaxFilingStatusEnum,
} from '@prisma/client';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import {
  BaseAssignee,
  CorporationDataForFiling,
  Filing,
  FilingReviewStatus,
  FilingWorkflowStatus,
} from '../../../molecules';
import { PeriodRange } from './types';

export const parseSalesTaxApiFilingDataToStandardFilingData = (
  salesTaxFiling: SalesTaxFiling
): Filing<SalesTaxFiling> => {
  const {
    id,
    filing_id,
    filing_locked_at,
    form_title: filingName,
    filing_frequency: frequency,
    filing_due_date: dueDate,
    status,
    country,
    state,
    ServiceRequest: serviceRequestData,
    Corporation: corporationData,
    assignedUser,
    filedByAgent,
    last_updated_at: lastUpdated,
    filed_at: filedAt,
  } = salesTaxFiling;

  // Map filing status to FilingReviewStatus
  let filingStatus: FilingReviewStatus = FilingReviewStatus.ACCRUING;
  if (status) {
    switch (status) {
      case SalesTaxFilingStatusEnum.ACCRUING:
        filingStatus = FilingReviewStatus.ACCRUING;
        break;
      case SalesTaxFilingStatusEnum.ACTIVE:
        filingStatus = FilingReviewStatus.ACTIVE;
        break;
      case SalesTaxFilingStatusEnum.FILED:
        filingStatus = FilingReviewStatus.FILED;
        break;
      case SalesTaxFilingStatusEnum.OVERDUE:
        filingStatus = FilingReviewStatus.OVERDUE;
        break;
      default:
        filingStatus = FilingReviewStatus.ACCRUING;
    }
  }

  // Map service request status to FilingWorkflowStatus
  let filingWorkflowStatus: FilingWorkflowStatus =
    FilingWorkflowStatus.NOT_STARTED;
  if (serviceRequestData) {
    filingWorkflowStatus = serviceRequestData.status as FilingWorkflowStatus;
  }

  // Create assignees array from assignedUser
  const assignees: BaseAssignee[] = [];
  if (assignedUser) {
    assignees.push({
      id: assignedUser.id,
      name: assignedUser.name || '',
      email: assignedUser.email || '',
    });
  }

  // Create agent from assignedAgent if present
  let filingAgent: BaseAssignee | undefined;
  let assignedResponsibleAgent: BaseAssignee | undefined;
  if (filedByAgent) {
    filingAgent = {
      id: filedByAgent.id,
      name: filedByAgent.name || '',
      email: filedByAgent.email || '',
    };
  }

  if (serviceRequestData?.assignedAgentFirmUser) {
    assignedResponsibleAgent = {
      id: serviceRequestData.assignedAgentFirmUser.user.id,
      name: serviceRequestData.assignedAgentFirmUser.user.name || '',
      email: serviceRequestData.assignedAgentFirmUser.user.email || '',
    };
  }

  // Create corporation object
  const corporation: CorporationDataForFiling = {
    id: corporationData?.id || 0,
    companyDisplayName: corporationData?.companyLegalName || '',
    incorporationCountry: country || '',
  };

  // Create filing period string based on tax period dates
  // Format: YYYY-MM or YYYY-Q#

  let filingPeriod = '';
  if (salesTaxFiling.reporting_period_end_date) {
    const date = NonTimezoneSensitiveDate.fromISO8601String(
      salesTaxFiling.reporting_period_end_date
    );
    const month = date.getMonth();
    const year = date.getYear();
    // Check if the filing is quarterly
    if (frequency === 'ANNUAL_CALENDAR_YEAR') {
      filingPeriod = `${year}`;
    } else if (frequency === 'FISCAL_YEAR') {
      filingPeriod = `${year}-F${month}`;
    } else if (
      frequency === 'QUARTERLY' ||
      frequency === 'QUARTERLY_PREPAY_MONTHLY'
    ) {
      // get month and year from reporting_period_end_date which is in YYYY-MM-DD format

      let quarter: number;
      // context: https://linear.app/commenda/issue/TTO-8/ny-sales-tax-filing-quarterly-period-incorrectly-shows-2025-q4-instead
      if (state === 'NY') {
        // New York State uses a fiscal year running April 1st to March 31st.
        // Quarters: Apr-Jun (Q1), Jul-Sep (Q2), Oct-Dec (Q3), Jan-Mar (Q4)
        quarter = month <= 3 ? 4 : Math.ceil((month - 3) / 3);
      } else {
        // Standard calendar year quarters: Jan-Mar (Q1), Apr-Jun (Q2), Jul-Sep (Q3), Oct-Dec (Q4)
        quarter = Math.ceil(month / 3);
      }
      filingPeriod = `${year}-Q${quarter}`;
    } else if (frequency === 'SEMI_ANNUALLY') {
      filingPeriod = `${year}-H${month}`;
    } else {
      // For monthly or other frequencies, use year-month format
      filingPeriod = `${year}-${month.toString().padStart(2, '0')}`;
    }
  }

  // Determine jurisdiction from country and state

  const filingAgentName = filedByAgent?.name || '';
  const assignedResponsibleAgentName = assignedResponsibleAgent?.name || '';

  return {
    id,
    filing_id,
    filing_locked_at,
    jurisdiction: state,
    filingName,
    filingPeriod,
    frequency: frequency as SalesTaxFilingFrequencyEnum,
    dueDate,
    filingStatus,
    filingWorkflowStatus,
    filedBy: filingAgentName,
    filedByAgent: filingAgent,
    assignedResponsibleAgent: assignedResponsibleAgentName,
    metadata: {
      estimated_taxable_amount:
        Number(salesTaxFiling.estimated_taxable_amount) || 0,
      estimated_non_taxable_amount:
        Number(salesTaxFiling.estimated_non_taxable_amount) || 0,
      estimated_tax_collected:
        Number(salesTaxFiling.estimated_tax_collected) || 0,
      estimated_marketplace_amount:
        Number(salesTaxFiling.estimated_marketplace_amount) || 0,
      estimated_exempt_amount:
        Number(salesTaxFiling.estimated_exempt_amount) || 0,
      estimated_gross_amount:
        (Number(salesTaxFiling.estimated_taxable_amount) || 0) +
        (Number(salesTaxFiling.estimated_non_taxable_amount) || 0) +
        (Number(salesTaxFiling.estimated_marketplace_amount) || 0) +
        (Number(salesTaxFiling.estimated_exempt_amount) || 0),

      taxable_amount: Number(salesTaxFiling.taxable_amount) || 0,
      non_taxable_amount: Number(salesTaxFiling.non_taxable_amount) || 0,
      marketplace_amount: Number(salesTaxFiling.marketplace_amount) || 0,
      exempt_amount: Number(salesTaxFiling.exempt_amount) || 0,

      gross_amount:
        (Number(salesTaxFiling.taxable_amount) || 0) +
        (Number(salesTaxFiling.non_taxable_amount) || 0) +
        (Number(salesTaxFiling.marketplace_amount) || 0) +
        (Number(salesTaxFiling.exempt_amount) || 0),

      tax_paid: Number(salesTaxFiling.tax_paid) || 0,
      tax_liability: Number(salesTaxFiling.tax_liability) || 0,
      discount: Number(salesTaxFiling.discount) || 0,
      tax_remitted: Number(salesTaxFiling.tax_remitted) || 0,
      filing_transactions_count:
        Number(salesTaxFiling.filing_transactions_count) || 0,

      prepayment: Number(salesTaxFiling.prepayment) || 0,
      prior_payment: Number(salesTaxFiling.prior_payment) || 0,
      retail_delivery_fee: Number(salesTaxFiling.retail_delivery_fee) || 0,
      jurisdiction_issued_credit:
        Number(salesTaxFiling.jurisdiction_issued_credit) || 0,
      marketplace_payment: Number(salesTaxFiling.marketplace_payment) || 0,
      vendor_discount: Number(salesTaxFiling.vendor_discount) || 0,
      rounding: Number(salesTaxFiling.rounding) || 0,
      penalty: Number(salesTaxFiling.penalty) || 0,
      interest: Number(salesTaxFiling.interest) || 0,
      filing_confirmation_number:
        salesTaxFiling.filing_confirmation_number || '',
      payment_confirmation_number:
        salesTaxFiling.payment_confirmation_number || '',
      tax_period_end_date: salesTaxFiling.tax_period_end_date || '',
      tax_period_start_date: salesTaxFiling.tax_period_start_date || '',
      reporting_period_start_date:
        salesTaxFiling.reporting_period_start_date || '',
      reporting_period_end_date: salesTaxFiling.reporting_period_end_date || '',
    },

    commendapediaId: '', // Default empty value, update if available
    assignees,
    agent: assignedResponsibleAgent,
    corporation,
    filedAt,
    serviceRequest: {
      id: serviceRequestData?.id || '',
      status: serviceRequestData?.status,
      metadata: serviceRequestData?.metadata || {},
      taskGroups: serviceRequestData?.taskGroups || [],
      // Additional fields needed by task components
      salesTaxFilingId: filing_id,
      corporationId: corporationData?.id,
      companyId: serviceRequestData?.companyId,
      ChatRoom: serviceRequestData?.ChatRoom,
    },
    lastUpdated: lastUpdated.toISOString
      ? lastUpdated.toISOString()
      : lastUpdated.toString(),
  };
};

const parseSalesTaxApiFilingsToFilingsData = (
  salesTaxFilings: SalesTaxFiling[]
): Filing<SalesTaxFiling>[] => {
  return salesTaxFilings.map(parseSalesTaxApiFilingDataToStandardFilingData);
};

export const getPastFilingPeriodDetails = (
  dueDate: string
): {
  filingPeriod: string;
  startDate: string;
  endDate: string;
} => {
  if (!dueDate) return { filingPeriod: '', startDate: '', endDate: '' };

  const date = NonTimezoneSensitiveDate.fromISO8601String(dueDate);
  // NonTimezoneSensitiveDate.getMonth() returns 1-indexed (1-12)
  let month = date.getMonth();
  let year = date.getYear();

  // The due date is typically 1 month after the reporting period.
  // To get the PREVIOUS filing period, we need to go back 2 months from the due date.
  // Example: Due date Aug 20 -> Current reporting period is July -> Previous period is June
  month -= 1;
  if (month <= 0) {
    month += 12;
    year -= 1;
  }

  // Get start date (first day of month)
  const startDate = `${year}-${month.toString().padStart(2, '0')}-01`;

  // Get end date (last day of month)
  // Create a date for the first day of the next month, then subtract one day
  const endDateObj = new Date(year, month, 0);
  const endDate = `${year}-${month.toString().padStart(2, '0')}-${endDateObj.getDate()}`;

  // Return filing period in YYYY-MM format
  return {
    filingPeriod: `${year}-${month.toString().padStart(2, '0')}`,
    startDate,
    endDate,
  };
};

export const useSalesTaxFilingList = ({
  corporationId,
  period,
  status,
  frequency,
  jurisdiction,
  limit = 100,
}: {
  corporationId: number;
  period?: PeriodRange;
  status?: SalesTaxFilingStatusEnum[];
  frequency?: SalesTaxFilingFrequencyEnum[];
  jurisdiction?: string[];
  limit?: number;
}) => {
  const [currentPeriod, setCurrentPeriod] = useState<PeriodRange>({
    startDate: period?.startDate,
    endDate: period?.endDate,
  });

  // Calculate the "filed in" month (period start + 1 month) for taxPeriodEndDate
  const getFiledInMonthEndDate = (periodStartDate?: string): string => {
    if (!periodStartDate) return '';

    return moment(periodStartDate)
      .add(1, 'month')
      .endOf('month')
      .format('YYYY-MM-DD');
  };

  const [taxPeriodEndDate, setTaxPeriodEndDate] = useState<string>(
    getFiledInMonthEndDate(period?.startDate)
  );

  const [cursor, setCursor] = useState<string | null>(null);

  useEffect(() => {
    if (!period?.startDate || !period?.endDate) return;

    const startDate = moment(period.startDate)
      .startOf('month')
      .format('YYYY-MM-DD');
    const endDate = moment(period.endDate).endOf('month').format('YYYY-MM-DD');

    setCurrentPeriod({ startDate, endDate });

    // Set taxPeriodEndDate to the "filed in" month (period start + 1 month)
    const filedInMonthEndDate = getFiledInMonthEndDate(period.startDate);
    setTaxPeriodEndDate(filedInMonthEndDate);
  }, [period?.startDate, period?.endDate]);

  // Use the API hook to fetch filings
  const {
    filings: salesTaxFilings,
    nextCursor,
    total: totalFilings,
    isLoading,
    error,
    mutate: refetchFilingsBase,
  } = useFilingsList({
    cursor,
    limit,
    corporationId: +corporationId,
    taxPeriodEndDate,
    status,
    frequency,
    jurisdiction,
    includeServiceRequest: true,
    includeAgent: true,
    includeAssignee: true,
  });

  // Convert SalesTaxFiling to Filing
  const filings = parseSalesTaxApiFilingsToFilingsData(salesTaxFilings || []);

  const refetchFilings = useCallback(
    async (newPeriod?: PeriodRange) => {
      if (newPeriod && newPeriod.endDate !== currentPeriod.endDate) {
        const startDate = moment(newPeriod.startDate)
          .startOf('month')
          .format('YYYY-MM-DD');
        const endDate = moment(newPeriod.endDate)
          .endOf('month')
          .format('YYYY-MM-DD');
        setCurrentPeriod({ startDate, endDate });

        // Update taxPeriodEndDate to the "filed in" month
        const filedInMonthEndDate = getFiledInMonthEndDate(newPeriod.startDate);
        setTaxPeriodEndDate(filedInMonthEndDate);
        setCursor(null);
        return;
      }

      refetchFilingsBase();
    },
    [currentPeriod, refetchFilingsBase]
  );

  // Load next page of results
  const loadMore = useCallback(() => {
    if (nextCursor) {
      setCursor(nextCursor);
    }
  }, [nextCursor]);

  return {
    filings,
    totalFilings,
    isLoading,
    error,
    refetchFilings,
    loadMore,
    hasMore: !!nextCursor,
  };
};
